<template>
<v-card class="mx-auto" width="90%" style="margin-bottom: 15px;">
  <v-container fluid class="my-5">
    <v-layout row justify-center>
      <v-row>
        <v-col cols="12">
          <v-data-table :headers="headers" :items="pendingPlans" style="width: 100%;" sort-by="date_created" sort-desc>
            <template v-slot:item.date_created="{ item }">
              {{ formatDate(item.date_created) }}
            </template>
            <template v-slot:item.retry_count="{ item }">
              {{ item.retry_count || 0 }}
              <v-tooltip top max-width="420" v-if="item.retries_exhausted">
                <template v-slot:activator="{ on }">
                  <v-chip v-on="on" x-small color="red" text-color="white" class="ml-1">
                    {{ $t('views.adminPendingPlans.chipAbandoned') }}
                  </v-chip>
                </template>
                <div>{{ abandonedReason(item) }}</div>
                <div v-if="item.vm_error" class="caption">{{ item.vm_error }}</div>
              </v-tooltip>
            </template>
            <template v-slot:item.action="{ item }">
              <v-tooltip top v-if="(authUser.isAdmin || authUser.isScientist)">
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on" @click="triggerRestart(item)">
                    <v-icon small>mdi-restart</v-icon>
                  </v-btn>
                </template>
                {{ $t('views.adminPendingPlans.tooltipRestart') }}
              </v-tooltip>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on" @click.stop="deleteDialog = true; deleteItem = item">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>
                {{ $t('views.adminPendingPlans.tooltipDelete') }}
              </v-tooltip>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
        {{ snackText }}
      </v-snackbar>
    </v-layout>
  </v-container>

  <v-dialog v-model="deleteDialog" max-width="400" :retain-focus="false">
    <v-card>
      <v-card-title class="headline">{{ $t('views.adminPendingPlans.dialogWarningTitle') }}</v-card-title>
      <v-card-text>
        <div> {{ deleteMessage }} </div>
        <br/>
        {{ $t('views.adminPendingPlans.dialogDeleting') }} <b>{{ deleteItem ? deleteItem.name : '' }}</b>
        <br/>
        {{ $t('views.adminPendingPlans.dialogConfirmMessage') }}
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="gray darken-1" text @click="closeDeleteDialog">{{ $t('views.adminPendingPlans.dialogCancel') }}</v-btn>
        <v-btn color="red darken-1" text @click="confirmDelete">{{ $t('views.adminPendingPlans.dialogDelete') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</v-card>
</template>

<script>
import numbro from 'numbro'
import moment from 'moment'
import { mapActions, mapGetters } from 'vuex'

export default {
  data () {
    return {
      deleteDialog: false,
      deleteItem: null,
      deleteMessage: '',
      snack: false,
      snackColor: '',
      snackText: ''
    }
  },
  methods: {
    ...mapActions('admin', [
      'loadPendingPlans',
      'restartPendingPlan',
      'deletePendingPlan'
    ]),
    triggerRestart (item) {
      this.snackColor = 'orange'
      this.snackText = this.$t('views.adminPendingPlans.snackTryingRestart')
      this.snack = true
      this.restartPendingPlan({
        clientId: item.clientId,
        simulationId: item.simulationId,
        queueItemId: item.id
      }).then(() => {
        this.snackColor = 'green'
        this.snackText = this.$t('views.adminPendingPlans.snackPlanRestarted')
        this.snack = true
      }).catch(error => {
        if (error.message === 'deleted') {
          this.deleteMessage = this.$t('views.adminPendingPlans.dialogDeleteMessage')
          this.deleteDialog = true
          this.deleteItem = item
        } else {
          this.snackColor = 'red'
          this.snackText = error.message
          this.snack = true
        }
      })
    },
    triggerDelete (item) {
      this.snackColor = 'orange'
      this.snackText = this.$t('views.adminPendingPlans.snackDeletingPlan')
      this.snack = true
      this.deletePendingPlan({
        clientId: item.clientId,
        simulationId: item.simulationId,
        queueItemId: item.id
      }).then(() => {
        this.snackColor = 'green'
        this.snackText = this.$t('views.adminPendingPlans.snackPlanDeleted')
        this.snack = true
        this.closeDeleteDialog()
      }).catch(error => {
        this.snackColor = 'red'
        this.snackText = error.message
        this.snack = true
        this.closeDeleteDialog()
      })
    },
    closeDeleteDialog () {
      this.deleteDialog = false
      this.deleteItem = null
      this.deleteMessage = ''
    },
    confirmDelete () {
      if (this.deleteItem) {
        this.triggerDelete(this.deleteItem)
      }
    },
    formatValue: function (value) {
      return numbro(value).format({
        average: true,
        lowPrecision: false,
        mantissa: 1,
        optionalMantissa: true
      })
    },
    formatDate (date) {
      if (date) {
        return moment(date.toDate()).format('YYYY/MM/DD - HH:mm')
      } else {
        return ''
      }
    },
    // Why the item stopped being retried. An item given up on for an error no retry can fix carries
    // a code written by reportSpawnFailure; one that simply used up its automatic attempts carries
    // none, and its count is the whole explanation.
    abandonedReason (item) {
      if (item.retries_exhausted_reason) {
        // An unknown code is what a queue item written by a newer deploy looks like to a client that
        // has not been reloaded, so the raw value is shown rather than a missing translation key.
        const key = 'views.adminPendingPlans.abandonedReasons.' + item.retries_exhausted_reason
        return this.$te(key) ? this.$t(key) : item.retries_exhausted_reason
      }
      return this.$t('views.adminPendingPlans.tooltipAbandonedRetries', { count: item.retry_count || 0 })
    }
  },
  computed: {
    ...mapGetters('auth', [
      'authUser'
    ]),
    ...mapGetters('admin', [
      'pendingPlans'
    ]),
    headers () {
      return [
        { text: this.$t('views.adminPendingPlans.headerName'), value: 'name' },
        { text: this.$t('views.adminPendingPlans.headerClientId'), value: 'clientId' },
        { text: this.$t('views.adminPendingPlans.headerDateCreated'), value: 'date_created' },
        { text: this.$t('views.adminPendingPlans.headerRetries'), value: 'retry_count' },
        { text: this.$t('views.adminPendingPlans.headerActions'), value: 'action', sortable: false }
      ]
    }
  },
  created: function () {
    this.loadPendingPlans()
  }
}
</script>
